export interface CropRunCenter {
  face_id: string;
  /** Crop center, percent of the source width (0-100). */
  x: number;
  /** Crop center, percent of the source height (0-100). */
  y: number;
  /** Crop zoom; missing means 1. */
  zoom?: number;
}

/**
 * How far (percent of the frame) the same face may drift from where a crop
 * run started before the run must end. Below it, merging absorbs detection
 * jitter so the crop does not twitch. Matches the worker's
 * `DEFAULT_MIN_MOVE_PCT` in `deriveCameraMoves.js`.
 */
export const CROP_RUN_MAX_MOVE_PCT = 6;

/**
 * How much the zoom may differ from the run's before the run must end; a
 * push-in is a reframe even when the face stays put. Matches the worker's
 * `DEFAULT_MIN_ZOOM_DELTA` in `deriveCameraMoves.js`.
 */
export const CROP_RUN_MAX_ZOOM_DELTA = 0.15;

const zoomOf = (center: CropRunCenter): number => (
  typeof center.zoom === 'number' && Number.isFinite(center.zoom) ? center.zoom : 1
);

/**
 * Whether a scene's crop center continues the run started by `anchor`, so the
 * two can share one keyframe that keeps the anchor's center and zoom.
 *
 * A face id is a person, not a framing: the same speaker keeps their id when a
 * multi-camera video cuts from a wide shot to their close-up, where their face
 * sits somewhere else entirely, at another size. Merging on the id alone
 * pinned the close-up to the wide shot's position and zoom, framing an empty
 * part of the picture.
 *
 * Two missing centers continue a run (callers fill them from neighbors later);
 * one missing center ends it.
 *
 * Mirrored by the backend export (`src/lib/sameSubjectCropRun.js`).
 */
export function continuesCropRun(
  anchor: CropRunCenter | null | undefined,
  next: CropRunCenter | null | undefined,
): boolean {
  if (!anchor || !next) return !anchor && !next;
  if (anchor.face_id !== next.face_id) return false;
  if (Math.abs(zoomOf(next) - zoomOf(anchor)) >= CROP_RUN_MAX_ZOOM_DELTA) return false;
  return Math.hypot(next.x - anchor.x, next.y - anchor.y) < CROP_RUN_MAX_MOVE_PCT;
}
